import { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Btn, Loading, PageHero, SectionHead } from "@/components/public/Ui";
import { ConsentCheck, Field, FormBanner, Honeypot, SelectInput, TextArea, TextInput } from "@/components/public/FormKit";

const CONNECTIONS = [
  "Patient",
  "Family Supporter",
  "Caregiver",
  "Volunteer",
  "Healthcare Professional",
  "Community Partner",
  "Other",
];

export default function Testimonials() {
  const [page, setPage] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    connection_to_bka: "",
    island_location: "",
    rating: "",
    testimonial: "",
    consent: false,
    honeypot: "",
  });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  useEffect(() => {
    api.get("/public/pages/testimonials").then((r) => setPage(r.data)).catch(() => setPage(false));
    api.get("/public/testimonials", { params: { limit: 48 } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (form.testimonial.trim().length < 20) e.testimonial = "Please share at least a sentence or two.";
    if (!form.consent) e.consent = "Consent to publish is required before we can review your story.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner({ status: "idle", message: "" });
    if (!validate()) return;
    setBanner({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/public/testimonials", {
        ...form,
        rating: form.rating ? Number(form.rating) : null,
      });
      setBanner({ status: "ok", message: data.message });
      setForm({
        full_name: "",
        email: "",
        connection_to_bka: "",
        island_location: "",
        rating: "",
        testimonial: "",
        consent: false,
        honeypot: "",
      });
    } catch (err) {
      setBanner({ status: "error", message: apiError(err) });
    }
  };

  if (page === null) return <Loading />;
  const hero = page?.body_json?.hero || {};

  return (
    <>
      <Seo title={page?.seo_title} description={page?.seo_description} canonicalPath="/testimonials" />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1}
        line2={hero.heading_line_2}
        subheading={hero.subheading}
        image={mediaUrl(page?.hero_image_url)}
      />

      <div className="bka-container py-14 lg:py-20" data-testid="testimonials-page">
        {items.length > 0 && (
          <>
            <SectionHead overline="Approved Stories" title="Voices From Our Community" />
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {items.map((t) => (
                <figure
                  key={t.id}
                  data-testid={`testimonial-${t.id}`}
                  className="bka-card-lift rounded-2xl border border-slate-200 bg-white p-7"
                >
                  <Quote className="h-6 w-6 text-bka-orangeDark" />
                  {t.rating ? (
                    <div className="mt-3 flex gap-0.5" aria-label={`${t.rating} out of 5`}>
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <Star key={i} className="h-3.5 w-3.5 fill-bka-orange text-bka-orangeDark" />
                      ))}
                    </div>
                  ) : null}
                  <blockquote className="mt-4 text-sm leading-[1.8] text-bka-ink/85">“{t.testimonial}”</blockquote>
                  <figcaption className="mt-6 border-t border-slate-100 pt-4">
                    <p className="text-sm font-bold text-bka-navy">{t.full_name}</p>
                    <p className="text-xs text-bka-ink/60">
                      {[t.connection_to_bka, t.island_location].filter(Boolean).join(" · ")}
                    </p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="bg-bka-cream py-14 lg:py-20">
        <div className="bka-container max-w-2xl">
          <SectionHead
            overline="Share Your Story"
            title="Submit a Testimonial"
            text="Your story is reviewed by the association before anything appears on the site. Your email address is kept private and is never published."
          />
          <form onSubmit={submit} className="space-y-5" data-testid="testimonial-form" noValidate>
            <Honeypot value={form.honeypot} onChange={set("honeypot")} />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your Full Name" htmlFor="t-name" required error={errors.full_name}>
                <TextInput
                  id="t-name"
                  data-testid="testimonial-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  error={errors.full_name}
                  placeholder="Enter your full name"
                />
              </Field>
              <Field label="Your Email" htmlFor="t-email" required error={errors.email} hint="Never shown publicly.">
                <TextInput
                  id="t-email"
                  type="email"
                  data-testid="testimonial-email"
                  value={form.email}
                  onChange={set("email")}
                  error={errors.email}
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Your Connection to BKA" htmlFor="t-connection">
                <SelectInput
                  id="t-connection"
                  data-testid="testimonial-connection"
                  value={form.connection_to_bka}
                  onChange={set("connection_to_bka")}
                >
                  <option value="">Select one</option>
                  {CONNECTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="Island / Location" htmlFor="t-island">
                <TextInput
                  id="t-island"
                  data-testid="testimonial-island"
                  value={form.island_location}
                  onChange={set("island_location")}
                  placeholder="e.g. Nassau, Bahamas"
                />
              </Field>
            </div>
            <Field label="Rating (optional)" htmlFor="t-rating">
              <SelectInput id="t-rating" data-testid="testimonial-rating" value={form.rating} onChange={set("rating")}>
                <option value="">No rating</option>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>{`${n} star${n > 1 ? "s" : ""}`}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Your Testimonial" htmlFor="t-body" required error={errors.testimonial}>
              <TextArea
                id="t-body"
                data-testid="testimonial-body"
                value={form.testimonial}
                onChange={set("testimonial")}
                error={errors.testimonial}
                rows={6}
                placeholder="Tell us about your experience with the Bahamas Kidney Association…"
              />
            </Field>
            <ConsentCheck
              id="t-consent"
              testId="testimonial-consent"
              checked={form.consent}
              onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
              error={errors.consent}
            >
              I consent to the Bahamas Kidney Association publishing my testimonial and first name on its
              website. I understand my email address will be kept private.
            </ConsentCheck>
            <FormBanner status={banner.status} message={banner.message} testId="testimonial-banner" />
            <Btn testId="testimonial-submit" type="submit" disabled={banner.status === "loading"}>
              {banner.status === "loading" ? "Submitting…" : "Submit Testimonial"}
            </Btn>
          </form>
        </div>
      </div>
    </>
  );
}
